<script setup lang="ts">
import type Swiper from 'swiper'
import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'
import { breakpointsTailwind } from '@vueuse/core'
import Card from './Card/index.vue'
import { useElementalFeaturedFeed } from '~~/src/composables/query/useElementalFeaturedFeed'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'

import type { ElementalQuery } from '~~/src/api/elementals'

const props = defineProps<{
  isFeatured?: boolean
  elementalTypeId: ElementalTypeEnum
  label: string
  hiddeTopic?: boolean
}>()
defineEmits(['exploreAction'])
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const { elementalTypeId } = toRefs(props)
const route = useRoute()

const queryParams = computed(() => ({
  order_by: 'random',
  order_dir: undefined,
  is_featured: props.isFeatured,
  elemental_type_id: elementalTypeId.value,
  topic_id: undefined,
} as ElementalQuery))
const { data, isLoading } = useElementalFeaturedFeed(elementalTypeId.value, props.isFeatured, queryParams)

const isInLoading = computed(() => process.server || isLoading.value)

const containerRef = ref()
const { width } = useElementSize(containerRef)
const swiper = ref<Swiper>()
const swiperSpaceBetween = computed(() => {
  if (width.value > 1024)
    return 16
  else
    return 8
})
const swiperSlidesPerView = computed(() => {
  if (width.value > 1024)
    return 4
  else if (width.value > 768)
    return 3
  else
    return 2
})

const showExploreButton = computed(() => route.path === '/')
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- header -->
    <div class="flex gap-5 justify-between items-center">
      <HeadingV2 level="3" class="line-clamp-1 capitalize">
        {{ label }}
      </HeadingV2>

      <!-- desktop -->
      <div class="hidden md:flex flex-grow-1 justify-end items-center">
        <button v-if="showExploreButton" class="flex gap-1.5 items-center" @click="() => $emit('exploreAction')">
          <TextV2 class="body-12 font-bold">
            Explore {{ label }}
          </TextV2>
          <div i-mdi:chevron-right class="w-4 h-4" />
        </button>
      </div>
      <!-- mobile -->
      <div v-if="showExploreButton" class="md:hidden">
        <button class="flex gap-1.5 items-center" @click="() => $emit('exploreAction')">
          <TextV2 class="body-12 font-bold capitalize">
            Explore All
          </TextV2>
          <div i-mdi:chevron-right class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Card list -->
    <div v-if="isInLoading" class="grid grid-cols-2 gap-2 md:gap-4 md:grid-cols-3 lg:grid-cols-4 ">
      <div v-for="n in 4" :key="n" class="flex flex-col gap-1 md:gap-2 group transition-all">
        <div
          class="w-full  md:min-w-[300px] md:min-h-[210px] aspect-3/2 rounded-2 bg-grayLighter animate-pulse"
        />

        <div class="flex flex-col gap-0.5">
          <div class="w-full h-4 bg-grayLighter rounded animate-pulse" />
          <div class="w-full h-4 bg-grayLighter rounded animate-pulse" />
        </div>
      </div>
    </div>

    <div v-if="data?.length" ref="containerRef" class="relative">
      <template v-if="isMobile">
        <div class="grid grid-cols-2 gap-2">
          <template
            v-for="(item, index) in data"
            :key="item.id"
          >
            <Card
              v-if="index < 4"
              :is-featured="isFeatured"
              :elemental="item"
            />
          </template>
        </div>
      </template>
      <!-- Desktop -->
      <template v-else>
        <SwiperContainer
          v-bind="{
            loop: false,
            slidesPerGroup: swiperSlidesPerView,
            slidesPerView: swiperSlidesPerView,
            spaceBetween: swiperSpaceBetween,
            autoplay: false,
            allowTouchMove: data.length > 1,
          }"
          class="relative isolate"
          @swiper="swiper = $event"
        >
          <SwiperSlide v-for="item in data" :key="item.id">
            <Card :is-featured="isFeatured" :elemental="item as Elemental" />
          </SwiperSlide>
        </SwiperContainer>

        <button
          class="w-10 h-10 flex center bg-white border-1 border-softGray hover:bg-softGray rounded-full absolute top-[calc(50%-30px)] translate-y-[-50%] -left-5 z-2"
          :class="{ 'opacity-0': swiper?.isBeginning }"
          @click="() => swiper?.slidePrev()"
        >
          <div i-mdi:chevron-left class="text-[22px] text-softBlack" />
        </button>

        <button
          class="w-10 h-10 flex center bg-white border-1 border-softGray hover:bg-softGray rounded-full absolute top-[calc(50%-30px)] translate-y-[-50%] -right-5 z-2"
          :class="{ 'opacity-0': swiper?.isEnd }"
          @click="() => swiper?.slideNext()"
        >
          <div i-mdi:chevron-right class="text-[22px] text-softBlack" />
        </button>
      </template>
    </div>
    <div v-if="!isInLoading && (!data || data?.length < 1)" class="flex center p-7 gap-2 min-h-[210px] border-1 border-softGray rounded-3">
      <TextV2 class="body-14">
        🤔
      </TextV2>
      <TextV2 class="body-14 text-center text-darkerGray">
        Your search returned no results
      </TextV2>
    </div>
  </div>
</template>
